import React, { useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, GlobalStyles, Typography } from "@mui/material";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import katex from "katex";
import "katex/dist/katex.min.css";
import { sanitizeQuestionHtml, mathTextSx } from "./questionHtml";
import { mathmlToLatex } from "./mathmlToLatex";
import MathField from "./MathField";

// Quill's built-in "formula" blot renders through window.katex — wiring it up
// here is what lets the toolbar's fx button turn LaTeX into a math formula.
window.katex = katex;

// Questions imported from docx carry equations as MathML, which Quill has no blot for (it would flatten
// it to loose text). On load each <math> is converted to LaTeX and held as a normal, editable formula.
// An equation the converter can't handle is kept as an inline MathML embed that is saved back unchanged
// (it can be replaced by typing LaTeX, but not edited in place).
const Embed = Quill.import("blots/embed");
const Delta = Quill.import("delta");

class MathMlBlot extends Embed {
  static create(value) {
    const node = super.create();
    const html = sanitizeQuestionHtml(value);
    node.setAttribute("data-value", html);
    node.innerHTML = html;
    return node;
  }

  static value(node) {
    return node.getAttribute("data-value");
  }
}
MathMlBlot.blotName = "mathml";
MathMlBlot.tagName = "SPAN";
MathMlBlot.className = "ql-mathml";
Quill.register(MathMlBlot, true);

const isValidLatex = (latex) => {
  try {
    katex.renderToString(latex, { throwOnError: true, strict: "ignore" });
    return true;
  } catch (e) {
    return false;
  }
};

const mathToDelta = (node) => {
  const latex = mathmlToLatex(node);
  if (latex && isValidLatex(latex)) return new Delta().insert({ formula: latex });
  return new Delta().insert({ mathml: node.outerHTML });
};

const CLIPBOARD = { matchVisual: false, matchers: [["math", mathToDelta]] };

const FULL_TOOLBAR = [
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["image", "formula"],
  ["clean"],
];

const COMPACT_TOOLBAR = [["bold", "italic"], ["image", "formula"], ["clean"]];

const FORMATS = ["bold", "italic", "underline", "list", "bullet", "image", "formula", "mathml"];

// Quill reports an untouched editor as "<p><br></p>" rather than "" — this
// treats that as empty so required-field checks work as expected.
export function isRichTextEmpty(html) {
  if (!html) return true;
  return html.replace(/<(.|\n)*?>/g, "").trim().length === 0;
}

function FormulaDialog({ edit, onClose, onSave }) {
  const [latex, setLatex] = useState(edit.latex);
  const trimmed = latex.trim();

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" disableEnforceFocus>
      {/* MathLive's on-screen keyboard lives on the page body; keep it above the dialog. */}
      <GlobalStyles styles={{ ":root": { "--keyboard-zindex": 2000 } }} />
      <DialogTitle>{edit.isNew ? "Insert equation" : "Edit equation"}</DialogTitle>
      <DialogContent>
        {edit.legacy && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This equation couldn&apos;t be converted automatically. Enter it again below to replace it.
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          Type the equation, or use the keyboard button at the right of the box for fractions, roots, powers and symbols.
        </Typography>
        <Box sx={{ mt: 1, border: "1px solid #c4c4c4", borderRadius: 1, minHeight: 64 }}>
          <MathField value={edit.latex} onChange={setLatex} autoFocus />
        </Box>
      </DialogContent>
      <DialogActions>
        {!edit.isNew && (
          <Button color="error" onClick={() => onSave("")} sx={{ mr: "auto" }}>
            Remove
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!trimmed} onClick={() => onSave(trimmed)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

FormulaDialog.propTypes = {
  edit: PropTypes.shape({ latex: PropTypes.string, legacy: PropTypes.bool, isNew: PropTypes.bool }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

function RichTextEditor({ value, onChange, placeholder, minHeight = 120, compact = false }) {
  const quillRef = useRef(null);
  const [edit, setEdit] = useState(null);

  // The toolbar's fx button opens the visual equation editor instead of Quill's LaTeX prompt.
  const modules = useMemo(
    () => ({
      toolbar: {
        container: compact ? COMPACT_TOOLBAR : FULL_TOOLBAR,
        handlers: {
          formula: () => {
            const range = quillRef.current?.getEditor().getSelection(true);
            setEdit({ index: range ? range.index : 0, latex: "", isNew: true });
          },
        },
      },
      clipboard: CLIPBOARD,
    }),
    [compact],
  );

  // Clicking an equation opens it for editing.
  const handleClick = (e) => {
    e.stopPropagation();
    const target = e.target.closest?.(".ql-formula, .ql-mathml");
    const quill = quillRef.current?.getEditor();
    const blot = target && quill ? Quill.find(target) : null;
    if (!blot) return;
    const legacy = target.classList.contains("ql-mathml");
    setEdit({ index: quill.getIndex(blot), latex: legacy ? "" : target.getAttribute("data-value") || "", legacy });
  };

  const saveFormula = (latex) => {
    const quill = quillRef.current.getEditor();
    if (!edit.isNew) quill.deleteText(edit.index, 1, "user");
    if (latex) {
      quill.insertEmbed(edit.index, "formula", latex, "user");
      quill.setSelection(edit.index + 1, 0, "silent");
    }
    setEdit(null);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        "& .ql-toolbar": { borderTopLeftRadius: 8, borderTopRightRadius: 8, backgroundColor: "#fafafa" },
        "& .ql-container": { borderBottomLeftRadius: 8, borderBottomRightRadius: 8, fontSize: "0.9rem" },
        "& .ql-editor": { minHeight, ...mathTextSx },
        "& .ql-formula, & .ql-mathml": { cursor: "pointer" },
      }}
    >
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={FORMATS}
        placeholder={placeholder}
      />
      {edit && <FormulaDialog edit={edit} onClose={() => setEdit(null)} onSave={saveFormula} />}
    </Box>
  );
}

RichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  minHeight: PropTypes.number,
  compact: PropTypes.bool,
};

export default RichTextEditor;
